/**
 * usePreviousCard
 * Fetches a display-only hint about the user's most-recently-saved card
 * ({ last4, brand }) so card-entry UIs can be reframed as "re-verify your card
 * ending in ••XXXX" instead of cold re-entry.
 *
 * Display-only: the returned data is NEVER a chargeable token — after the Stripe
 * account switch the saved card must be re-entered to attach to the new account.
 * Failures are swallowed (returns null) so this can never block a payment flow.
 */

import { useState, useEffect } from 'react'
import { getPreviousCard } from '../api/paymentMethods'
import type { PreviousCard } from '../types/paymentMethod'

export function usePreviousCard(enabled: boolean = true): { card: PreviousCard | null; loading: boolean } {
  const [card, setCard] = useState<PreviousCard | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return

    const token = localStorage.getItem('auth_token')
    if (!token) return

    let cancelled = false
    setLoading(true)

    getPreviousCard(token)
      .then((res) => {
        if (!cancelled) setCard(res.card)
      })
      .catch(() => {
        if (!cancelled) setCard(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  return { card, loading }
}

/**
 * Nicely capitalize a Stripe card brand for display
 * (e.g. "visa" -> "Visa", "amex" -> "Amex", "american_express" -> "American Express").
 */
export function formatCardBrand(brand: string | undefined | null): string {
  if (!brand) return 'card'
  return brand
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}
